import { addBreadcrumb, captureException, captureMessage } from "./sentry";
import { LogCategory, LogLevel } from "./types";
import type {
  ErrorInfo,
  LogContext,
  PerformanceMetric,
} from "./types";

/**
 * Centralized logging service for the application
 * Sends logs to Sentry when configured, falls back to console in development
 */
class Logger {
  private isDevelopment = import.meta.env.DEV;

  /**
   * Get current user context from the app state
   * This should be enhanced to pull from your auth/user store
   */
  private getBaseContext(): LogContext {
    try {
      // TODO: Extract from LoginStore or AuthContext
      // For now, return minimal context
      return {
        environment: import.meta.env.MODE,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      return {};
    }
  }

  /**
   * Merge base context with provided context
   */
  private mergeContext(context?: LogContext): LogContext {
    return {
      ...this.getBaseContext(),
      ...context,
    };
  }

  /**
   * Send log to Sentry
   */
  private sendToSentry(
    level: LogLevel,
    message: string,
    data?: any,
    context?: LogContext
  ): void {
    const mergedContext = this.mergeContext(context);
    const logData = {
      ...mergedContext,
      ...data,
    };

    // Add breadcrumb for non-error logs
    if (level !== LogLevel.ERROR) {
      addBreadcrumb({
        message,
        category: data?.category || "log",
        level: level as any,
        data: logData,
      });
    }

    // Send errors to Sentry
    if (level === LogLevel.ERROR) {
      if (data?.error instanceof Error) {
        captureException(data.error, logData);
      } else {
        captureMessage(message, "error", logData);
      }
    } else if (level === LogLevel.WARN) {
      captureMessage(message, "warning", logData);
    }

    // Always log to console in development
    if (this.isDevelopment) {
      const consoleMethod = level === LogLevel.DEBUG ? "debug" : level;
      console[consoleMethod](`[${level.toUpperCase()}] ${message}`, logData);
    }
  }

  /**
   * Log a general event
   */
  logEvent(
    category: LogCategory | string,
    action: string,
    data?: any,
    context?: LogContext
  ): void {
    this.sendToSentry(
      LogLevel.INFO,
      `${category}.${action}`,
      {
        category,
        action,
        ...data,
      },
      context
    );
  }

  /**
   * Log an error
   */
  logError(
    error: Error | ErrorInfo | string,
    category?: LogCategory | string,
    context?: LogContext
  ): void {
    let errorInfo: ErrorInfo;
    let errorObject: Error | undefined;

    if (typeof error === "string") {
      errorInfo = { message: error };
      errorObject = new Error(error);
    } else if (error instanceof Error) {
      errorInfo = {
        message: error.message,
        stack: error.stack,
        // @ts-ignore - some errors have code property
        code: error.code,
      };
      errorObject = error;
    } else {
      errorInfo = error;
      errorObject = new Error(error.message);
    }

    this.sendToSentry(
      LogLevel.ERROR,
      errorInfo.message,
      {
        category: category || LogCategory.ERROR,
        error: errorObject,
        errorInfo,
      },
      context
    );
  }

  /**
   * Log a performance metric
   */
  logPerformance(metric: PerformanceMetric, _context?: LogContext): void {
    // Add as breadcrumb for context
    addBreadcrumb({
      message: `Performance: ${metric.name}`,
      category: "performance",
      level: "info",
      data: {
        metric: metric.name,
        duration: metric.duration,
        ...metric.metadata,
      },
    });

    // Also log to console in development
    if (this.isDevelopment) {
      console.info(
        `[PERFORMANCE] ${metric.name}: ${metric.duration.toFixed(2)}ms`,
        metric.metadata
      );
    }
  }

  /**
   * Log debug information (only in development)
   */
  logDebug(message: string, data?: any, _context?: LogContext): void {
    if (this.isDevelopment) {
      console.debug(`[DEBUG] ${message}`, data);
    }
  }

  /**
   * Log info message
   */
  info(message: string, data?: any, context?: LogContext): void {
    this.sendToSentry(LogLevel.INFO, message, data, context);
  }

  /**
   * Log warning message
   */
  warn(message: string, data?: any, context?: LogContext): void {
    this.sendToSentry(LogLevel.WARN, message, data, context);
  }

  /**
   * Log API request
   */
  logApiRequest(
    method: string,
    url: string,
    data?: any,
    _context?: LogContext
  ): void {
    addBreadcrumb({
      message: `API Request: ${method} ${url}`,
      category: "http",
      level: "info",
      data: {
        method,
        url,
        ...data,
      },
    });
  }

  /**
   * Log API response
   */
  logApiResponse(
    method: string,
    url: string,
    status: number,
    duration: number,
    data?: any,
    _context?: LogContext
  ): void {
    const isSuccess = status >= 200 && status < 300;

    addBreadcrumb({
      message: `API Response: ${method} ${url} - ${status}`,
      category: "http",
      level: isSuccess ? "info" : "warning",
      data: {
        method,
        url,
        status,
        duration,
        ...data,
      },
    });

    // Log slow requests as warnings
    if (duration > 3000) {
      this.warn(`Slow API request: ${method} ${url}`, {
        duration,
        status,
      });
    }
  }

  /**
   * Log API error
   */
  logApiError(
    method: string,
    url: string,
    error: any,
    context?: LogContext
  ): void {
    const errorMessage = `API Error: ${method} ${url}`;

    // Create or use error object
    const errorObj = error instanceof Error
      ? error
      : new Error(errorMessage);

    this.logError(
      errorObj,
      LogCategory.API_ERROR,
      {
        ...context,
        method,
        url,
        statusCode: error?.response?.status,
        responseData: error?.response?.data,
      }
    );
  }

  /**
   * Measure and log execution time of an async function
   */
  async measureAsync<T>(
    name: string,
    fn: () => Promise<T>,
    context?: LogContext
  ): Promise<T> {
    const startTime = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - startTime;

      this.logPerformance(
        {
          name,
          duration,
          timestamp: Date.now(),
        },
        context
      );
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;

      this.logError(
        error as Error,
        LogCategory.PERFORMANCE,
        {
          ...context,
          operation: name,
          duration,
        }
      );
      throw error;
    }
  }

  /**
   * Measure and log execution time of a sync function
   */
  measure<T>(name: string, fn: () => T, context?: LogContext): T {
    const startTime = performance.now();
    try {
      const result = fn();
      const duration = performance.now() - startTime;
      this.logPerformance(
        {
          name,
          duration,
          timestamp: Date.now(),
        },
        context
      );
      return result;
    } catch (error) {
      const duration = performance.now() - startTime;
      this.logError(
        error as Error,
        LogCategory.PERFORMANCE,
        {
          ...context,
          operation: name,
          duration,
        }
      );
      throw error;
    }
  }
}

// Export singleton instance
export const logger = new Logger();
